import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import { CheckCircle2, Loader2, AlertCircle, Receipt } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

function formatUsd(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

export default function PortalBillingReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState("checking"); // checking | paid | expired | timeout | error
  const [statusInfo, setStatusInfo] = useState(null);
  const [error, setError] = useState("");
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    if (!sessionId) {
      setPhase("error");
      setError("Missing session_id in URL.");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      if (stopRef.current) return;
      try {
        const r = await api.get(`/payments/checkout/status/${sessionId}`);
        setStatusInfo(r.data);
        const ps = r.data?.payment_status;
        const st = r.data?.status;
        if (ps === "paid") {
          setPhase("paid");
          return;
        }
        if (st === "expired") {
          setPhase("expired");
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setPhase("timeout");
          return;
        }
        attempts += 1;
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setError(formatApiError(e));
        setPhase("error");
      }
    };
    poll();

    return () => {
      stopRef.current = true;
    };
  }, [sessionId]);

  const renderBody = () => {
    if (phase === "checking") {
      return (
        <div className="flex items-center gap-3 text-slate-700">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <div>
            <div className="font-semibold">Confirming your payment with Stripe…</div>
            <div className="text-sm text-slate-500">This usually takes a few seconds.</div>
          </div>
        </div>
      );
    }
    if (phase === "paid") {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-emerald-700">
            <CheckCircle2 className="w-6 h-6" />
            <div>
              <div className="font-semibold text-lg text-slate-900">Payment successful</div>
              <div className="text-sm text-slate-600">
                {statusInfo?.kind === "platform_plan"
                  ? `Your ${statusInfo?.package_name || "plan"} is now active.`
                  : `${statusInfo?.program_name || "Program"} activation paid.`}
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-1">
            <div className="flex justify-between"><span>Amount</span><span className="font-semibold">{formatUsd(statusInfo?.amount_total)}</span></div>
            <div className="flex justify-between"><span>Reference</span><span className="font-mono text-xs">{sessionId}</span></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="return-go-billing"
              onClick={() => navigate("/portal/billing")}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800"
            >
              <Receipt className="w-4 h-4" /> View receipts
            </button>
            <button
              type="button"
              onClick={() => navigate("/portal")}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Back to Intelligence Center
            </button>
          </div>
        </div>
      );
    }
    if (phase === "expired") {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-amber-700">
            <AlertCircle className="w-6 h-6" />
            <div className="font-semibold text-slate-900">Checkout session expired</div>
          </div>
          <div className="text-sm text-slate-600">
            Your Stripe session timed out before payment was completed. You can safely try again.
          </div>
          <button
            type="button"
            onClick={() => navigate("/portal/billing")}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800"
          >
            Back to Billing
          </button>
        </div>
      );
    }
    if (phase === "timeout") {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-amber-700">
            <AlertCircle className="w-6 h-6" />
            <div className="font-semibold text-slate-900">Still verifying</div>
          </div>
          <div className="text-sm text-slate-600">
            We couldn’t confirm the payment quickly enough. If your card was charged, you’ll see a
            receipt on the Billing page within a minute.
          </div>
          <button
            type="button"
            onClick={() => navigate("/portal/billing")}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800"
          >
            Go to Billing
          </button>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 text-rose-700">
          <AlertCircle className="w-6 h-6" />
          <div className="font-semibold text-slate-900">Something went wrong</div>
        </div>
        <div className="text-sm text-slate-600">{error || "We couldn’t confirm the payment status."}</div>
        <button
          type="button"
          onClick={() => navigate("/portal/billing")}
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800"
        >
          Back to Billing
        </button>
      </div>
    );
  };

  return (
    <ToolPageShell eyebrow="Billing" title="Payment confirmation" intro="Hold tight while we finalize your transaction with Stripe.">
      <div data-testid="billing-return-card" className="rounded-2xl border border-slate-200 bg-white p-6 max-w-xl">
        {renderBody()}
      </div>
    </ToolPageShell>
  );
}
